<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', $event)" size="medium" :zIndex="1200" isWithScroll :isClosable="false">
    <template #header>
      <div class="location-editor__header">
        <h2 class="global-h2">{{ props.location ? "Adjust Location" : "Add Social Handles" }}</h2>
        <p>Set the location's social handles to use in your posts.</p>
      </div>
    </template>
    <template #content>
      <div class="location-editor">
        <div v-if="showIdentity" class="location-editor__identity">
          <div class="location-editor__avatar-wrap">
            <LocationAvatar :src="previewIcon" :name="name || 'Location'" :size="56" placeholderTone="gray" />
            <button v-if="isNameEditMode && previewIcon" type="button" class="location-editor__clear-icon" aria-label="Clear location icon" @mousedown.prevent @click="clearIcon">
              <IconEmbedded name="remove_2" :size="12" color="#667085" />
            </button>
          </div>
          <div v-if="name.trim() && !isEditingName" class="location-editor__name-display">
            <span>{{ name }}</span>
            <button type="button" aria-label="Edit Location name" @click="beginNameEdit">
              <IconEmbedded name="edit-pencil_2-5" :size="17" />
            </button>
          </div>
          <label v-else class="location-editor__field location-editor__field--grow" aria-label="Location name">
            <input
              ref="nameInput"
              v-model="name"
              class="global-text-input"
              maxlength="150"
              placeholder="Business or place name"
              @focus="isEditingName = true"
              @blur="finishNameEdit"
              @keydown.enter.prevent="finishNameEdit"
            />
          </label>
        </div>

        <label class="location-editor__field">
          <span>Instagram handle</span>
          <div class="location-editor__handle-input">
            <span>@</span>
            <input v-model="instagramHandle" type="text" placeholder="username" @input="onInstagramInput" @blur="handleInstagramBlur" />
            <LoadingIcon v-if="isCheckingInstagram" class="location-editor__inline-loader" :size="18" />
          </div>
          <p
            v-if="instagramStatusMessage"
            class="location-editor__status-row"
            :class="[
              instagramState === 'valid' ? 'location-editor__valid' : '',
              instagramState === 'invalid' ? 'location-editor__instagram-guidance' : '',
              instagramState === 'error' ? 'global-error-string' : '',
            ]"
          >
            {{ instagramStatusMessage }}
          </p>
          <a v-if="instagramProfileUrl" class="location-editor__url-link" :href="instagramProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
            {{ instagramDisplayUrl }}
            <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
          </a>
        </label>

        <div class="location-editor__field location-editor__field--facebook">
          <span class="location-editor__fb-label">Facebook Page</span>

          <template v-if="!selectedFacebookName">
            <div class="location-editor__fb-tabs" role="tablist" aria-label="Facebook Page input mode">
              <button
                type="button"
                role="tab"
                :aria-selected="facebookMode === 'search'"
                class="location-editor__fb-tab"
                :class="{ 'location-editor__fb-tab--active': facebookMode === 'search' }"
                @click="setFacebookMode('search')"
              >
                Search
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="facebookMode === 'manual'"
                class="location-editor__fb-tab"
                :class="{ 'location-editor__fb-tab--active': facebookMode === 'manual' }"
                @click="setFacebookMode('manual')"
              >
                Enter manually
              </button>
            </div>

            <div v-if="facebookMode === 'search'" class="location-editor__fb-panel">
              <input v-model="facebookQuery" class="location-editor__fb-search-input" type="text" placeholder="Search Facebook Pages" @input="queueFacebookSearch" />
              <div v-if="isSearchingFacebook" class="location-editor__fb-status"><LoadingIcon :size="18" /> Searching...</div>
              <div v-for="page in facebookResults" :key="page.pageId" class="location-editor__fb-result">
                <div class="location-editor__fb-result-info">
                  <span class="location-editor__fb-name">{{ page.name }}</span>
                  <span v-if="deriveFacebookVanity(page.url)" class="location-editor__fb-handle">{{ deriveFacebookVanity(page.url) }}</span>
                  <small v-if="page.address">{{ page.address }}</small>
                </div>
                <SrpButton size="small" color="gray" fill="outlined" @click="selectFacebookPage(page)">Select</SrpButton>
              </div>
            </div>

            <template v-else>
              <input
                v-model="facebookManualInput"
                class="global-text-input"
                type="text"
                placeholder="Paste a Facebook URL, handle, or Page ID"
                @keydown.enter.prevent="resolveFacebookManualInput"
                @blur="resolveFacebookManualInput"
              />
              <div v-if="isSearchingFacebook" class="location-editor__fb-status location-editor__fb-status--bare"><LoadingIcon :size="18" /> Resolving...</div>
            </template>
          </template>

          <template v-else>
            <div class="location-editor__fb-selected">
              <div class="location-editor__fb-selected-info">
                <strong>{{ selectedFacebookName }}</strong>
                <a v-if="facebookDisplayUrl" class="location-editor__url-link" :href="facebookProfileUrl || undefined" target="_blank" rel="noopener noreferrer" @click.stop>
                  {{ facebookDisplayUrl }}
                  <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                </a>
              </div>
              <button type="button" class="location-editor__fb-change" @click="changeFacebookPage">Change</button>
            </div>
            <p v-if="isFacebookVerified" class="location-editor__fb-verified"><IconEmbedded name="check-circle_2-5" :size="18" color="#21883f" /> Facebook Page verified</p>
          </template>
        </div>

        <label class="location-editor__field">
          <span>TikTok handle</span>
          <div class="location-editor__handle-input">
            <span>@</span>
            <input v-model="tikTokHandle" type="text" placeholder="username" @input="onTikTokInput" @blur="revealIdentityAfterHandleBlur(tikTokHandle)" />
          </div>
          <a v-if="tikTokProfileUrl" class="location-editor__url-link" :href="tikTokProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
            {{ tikTokDisplayUrl }}
            <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
          </a>
        </label>

        <p v-if="errorMessage" class="global-error-string">{{ errorMessage }}</p>
      </div>
    </template>
    <template #footer="{ closeModal }">
      <div class="location-editor__footer">
        <SrpButton color="gray" fill="outlined" :isDisabled="isSaving" @click="closeModal">Cancel</SrpButton>
        <SrpButton :isDisabled="isSaving || !canSave" @click.stop="save">
          <template #icon><LoadingIcon v-if="isSaving" :size="18" /><IconEmbedded v-else name="save_2-5" :size="18" /></template>
          Save location
        </SrpButton>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationAvatar from "./LocationAvatar.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import SocialFieldInputCleaner, { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl } from "@logic/SocialFieldInputCleaner";
import type { FacebookPageSearchResult, LocationData } from "@contracts/socialLocations";

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; location?: LocationData | null }>(), { location: null });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "saved", location: LocationData): void;
}>();

const name = ref("");
const nameInput = ref<HTMLInputElement | null>(null);
const isEditingName = ref(false);
const instagramHandle = ref("");
const tikTokHandle = ref("");
const instagramState = ref<"unchecked" | "valid" | "invalid" | "error">("unchecked");
const instagramMessage = ref("");
const previewIcon = ref<string | null>(null);
const iconId = ref<string | null>(null);
const isCheckingInstagram = ref(false);
const facebookMode = ref<"search" | "manual">("search");
const facebookQuery = ref("");
const facebookManualInput = ref("");
const facebookResults = ref<FacebookPageSearchResult[]>([]);
const selectedFacebookToken = ref<string | null>(null);
const selectedFacebookPage = ref<FacebookPageSearchResult | null>(null);
const selectedFacebookName = ref("");
const clearExistingFacebook = ref(false);
const isFacebookVerified = ref(false);
const isSearchingFacebook = ref(false);
const isSaving = ref(false);
const errorMessage = ref("");
const wasHandleBlurredWithValue = ref(false);
const lastCheckedInstagramHandle = ref("");
let facebookTimer: ReturnType<typeof setTimeout> | null = null;
let facebookSearchSequence = 0;

watch(
  () => [props.isVisible, props.location?.id] as const,
  ([visible]) => {
    if (!visible) return;
    const location = props.location;
    name.value = location?.name ?? "";
    instagramHandle.value = normalizeInstagramHandle(location?.socialSet?.instagramHandle ?? "");
    tikTokHandle.value = normalizeTikTokHandle(location?.socialSet?.tikTokHandle ?? "");
    instagramState.value = location?.socialSet?.instagramValidatedAt ? "valid" : "unchecked";
    lastCheckedInstagramHandle.value = normalizeInstagramHandle(location?.socialSet?.instagramHandle ?? "");
    previewIcon.value = location?.icon ?? null;
    iconId.value = location?.icon ?? null;
    selectedFacebookToken.value = null;
    selectedFacebookPage.value = null;
    selectedFacebookName.value = location?.socialSet?.facebookPageName ?? "";
    clearExistingFacebook.value = false;
    isFacebookVerified.value = false;
    facebookMode.value = "search";
    facebookQuery.value = "";
    facebookManualInput.value = "";
    facebookResults.value = [];
    errorMessage.value = "";
    wasHandleBlurredWithValue.value = false;
    isEditingName.value = false;
  },
  { immediate: true }
);

const hasIdentity = computed(() => Boolean(instagramHandle.value.trim() || selectedFacebookName.value || tikTokHandle.value.trim()));
const showIdentity = computed(() => Boolean(props.location || selectedFacebookName.value || instagramState.value === "valid" || (wasHandleBlurredWithValue.value && hasIdentity.value)));
const isNameEditMode = computed(() => !name.value.trim() || isEditingName.value);
const canSave = computed(() => Boolean(name.value.trim() && hasIdentity.value));
const instagramProfileUrl = computed(() => buildInstagramProfileUrl(normalizeInstagramHandle(instagramHandle.value)));
const instagramDisplayUrl = computed(() => stripUrlProtocol(instagramProfileUrl.value));
const tikTokProfileUrl = computed(() => buildTikTokProfileUrl(normalizeTikTokHandle(tikTokHandle.value)));
const tikTokDisplayUrl = computed(() => stripUrlProtocol(tikTokProfileUrl.value));
const facebookProfileUrl = computed(() => {
  if (selectedFacebookPage.value) {
    return buildFacebookPageUrl(selectedFacebookPage.value.url, selectedFacebookPage.value.pageId);
  }
  if (clearExistingFacebook.value || !selectedFacebookName.value) return "";
  const socialSet = props.location?.socialSet;
  return buildFacebookPageUrl(socialSet?.facebookPageUrl, socialSet?.facebookPageId);
});
const facebookDisplayUrl = computed(() => {
  const vanity = deriveFacebookVanity(facebookProfileUrl.value);
  return vanity ? `facebook.com/${vanity}` : "";
});
const instagramStatusMessage = computed(() => {
  if (instagramState.value === "valid") return "✓ Found account";
  if (instagramState.value === "invalid") return "This handle isn't a Business account. Please set the location name manually.";
  if (instagramState.value === "error") return instagramMessage.value;
  return "";
});

function stripUrlProtocol(url: string): string {
  return url.replace(/^https?:\/\//i, "");
}

function revealIdentityAfterHandleBlur(value: string) {
  if (value.trim()) wasHandleBlurredWithValue.value = true;
}

function onInstagramInput() {
  instagramHandle.value = normalizeInstagramHandle(instagramHandle.value);
  instagramState.value = "unchecked";
  instagramMessage.value = "";
}

function onTikTokInput() {
  tikTokHandle.value = normalizeTikTokHandle(tikTokHandle.value);
}

async function handleInstagramBlur() {
  revealIdentityAfterHandleBlur(instagramHandle.value);
  const normalizedHandle = normalizeInstagramHandle(instagramHandle.value);
  if (!normalizedHandle) {
    instagramHandle.value = "";
    instagramState.value = "unchecked";
    instagramMessage.value = "";
    lastCheckedInstagramHandle.value = "";
    return;
  }
  instagramHandle.value = normalizedHandle;
  if (normalizedHandle === lastCheckedInstagramHandle.value || isCheckingInstagram.value) return;
  await checkInstagram(normalizedHandle);
}

async function beginNameEdit() {
  isEditingName.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
}

function finishNameEdit() {
  isEditingName.value = false;
}

function clearIcon() {
  previewIcon.value = null;
  iconId.value = null;
}

async function checkInstagram(handle: string) {
  isCheckingInstagram.value = true;
  errorMessage.value = "";
  try {
    const result = await SocialLocationsRepo.validateInstagram(props.customerId, handle);
    instagramState.value = result.isValid ? "valid" : "invalid";
    instagramMessage.value = result.message ?? "";
    lastCheckedInstagramHandle.value = handle;
    if (result.isValid) {
      instagramHandle.value = result.username;
      lastCheckedInstagramHandle.value = normalizeInstagramHandle(result.username);
      if (!name.value.trim() && result.name) name.value = result.name;
      iconId.value = result.iconId ?? null;
      if (result.iconId) previewIcon.value = result.iconId;
    }
  } catch (error: any) {
    instagramState.value = "error";
    instagramMessage.value = getErrorMessage(error);
  } finally {
    isCheckingInstagram.value = false;
  }
}

function normalizeInstagramHandle(value: string): string {
  return SocialFieldInputCleaner.cleanInstagramHandle(value).trim().replace(/^@+/, "").toLowerCase();
}

function normalizeTikTokHandle(value: string): string {
  return SocialFieldInputCleaner.cleanTikTokHandle(value).trim();
}

function setFacebookMode(mode: "search" | "manual") {
  if (facebookMode.value === mode) return;
  facebookMode.value = mode;
  if (facebookTimer) clearTimeout(facebookTimer);
  facebookSearchSequence++;
  facebookResults.value = [];
  isSearchingFacebook.value = false;
  errorMessage.value = "";
}

function queueFacebookSearch() {
  if (facebookTimer) clearTimeout(facebookTimer);
  const sequence = ++facebookSearchSequence;
  facebookResults.value = [];
  isSearchingFacebook.value = false;
  errorMessage.value = "";
  const input = facebookQuery.value.trim();
  if (input.length < 2) return;
  facebookTimer = setTimeout(() => runFacebookLookup(sequence, input, "search"), 350);
}

async function resolveFacebookManualInput() {
  if (facebookTimer) clearTimeout(facebookTimer);
  const input = normalizeFacebookResolveInput(facebookManualInput.value.trim());
  if (!input) return;

  const inputKind = classifyFacebookInput(input);
  if (inputKind !== "resolve") {
    errorMessage.value = facebookResolveErrorMessage;
    return;
  }

  const sequence = ++facebookSearchSequence;
  facebookResults.value = [];
  await runFacebookLookup(sequence, input, "resolve");
}

async function runFacebookLookup(sequence: number, input: string, inputKind: "search" | "resolve") {
  isSearchingFacebook.value = true;
  errorMessage.value = "";
  try {
    if (inputKind === "resolve") {
      const result = await SocialLocationsRepo.resolveFacebookPage(props.customerId, input, true);
      if (sequence === facebookSearchSequence) {
        selectFacebookPage(result);
        isFacebookVerified.value = true;
      }
    } else {
      const results = await SocialLocationsRepo.searchFacebookPages(props.customerId, input);
      if (sequence === facebookSearchSequence) facebookResults.value = results;
    }
  } catch (error: any) {
    if (sequence === facebookSearchSequence) errorMessage.value = inputKind === "resolve" ? facebookResolveErrorMessage : getErrorMessage(error);
  } finally {
    if (sequence === facebookSearchSequence) isSearchingFacebook.value = false;
  }
}

const facebookResolveErrorMessage = "Couldn't resolve that public Facebook Page. Try its handle, full URL, numeric Page ID, or search by name.";

function classifyFacebookInput(input: string): "search" | "resolve" | "partial-url" | "invalid-url" {
  if (/^[0-9]{5,25}$/.test(input)) return "resolve";
  if (/^[0-9]+$/.test(input)) return input.length < 5 ? "partial-url" : "invalid-url";
  if (isSupportedFacebookUrl(input)) return "resolve";
  if (isFacebookHandle(input)) return "resolve";

  const looksLikeFacebookUrl = /^(?:https?:\/\/)?(?:(?:www|m|web)\.)?(?:facebook\.com|fb\.com)(?:[/?#]|$)/i.test(input);
  if (looksLikeFacebookUrl) return "partial-url";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(input)) return "invalid-url";
  return "search";
}

const facebookAllowedHosts = new Set(["facebook.com", "www.facebook.com", "m.facebook.com", "web.facebook.com", "fb.com", "www.fb.com"]);
const facebookReservedPaths = new Set([
  "about",
  "ads",
  "business",
  "events",
  "groups",
  "help",
  "home.php",
  "login",
  "marketplace",
  "messages",
  "people",
  "photo",
  "photo.php",
  "photos",
  "plugins",
  "privacy",
  "profile.php",
  "reel",
  "reels",
  "share",
  "stories",
  "story.php",
  "watch",
  "permalink.php",
  "pages",
  "pg",
]);

function isFacebookHandle(input: string): boolean {
  const handle = input.replace(/^@+/, "").trim();
  if (!/^[A-Za-z0-9.]{2,75}$/.test(handle) || /^[0-9]+$/.test(handle)) return false;
  return !facebookReservedPaths.has(handle.toLowerCase()) && !facebookAllowedHosts.has(handle.toLowerCase());
}

function isSupportedFacebookUrl(input: string): boolean {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === "https:" || url.protocol === "http:") && facebookAllowedHosts.has(url.hostname.toLowerCase()) && url.pathname !== "/";
  } catch {
    return false;
  }
}

function isFacebookPageIdentitySegment(segment: string): boolean {
  if (facebookReservedPaths.has(segment.toLowerCase())) return false;
  return /^[0-9]{5,25}$/.test(segment) || /^[A-Za-z0-9.]{2,75}$/.test(segment);
}

/** Normalize pasteable Facebook URLs into a handle, Page ID, or cleaned vanity URL. */
function normalizeFacebookResolveInput(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!facebookAllowedHosts.has(url.hostname.toLowerCase())) return trimmed;
    const segments = url.pathname
      .split("/")
      .filter(Boolean)
      .map(s => decodeURIComponent(s));
    if (segments.length === 0) return trimmed;

    if (/^profile\.php$/i.test(segments[0])) {
      const id = url.searchParams.get("id")?.trim() ?? "";
      if (/^[0-9]{5,25}$/.test(id)) return id;
      return trimmed;
    }
    if (segments.length >= 3 && /^people$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[2])) return segments[2];
    if (segments.length >= 2 && /^pages$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[segments.length - 1])) {
      return segments[segments.length - 1];
    }
    if (segments.length >= 2 && /^pg$/i.test(segments[0]) && isFacebookPageIdentitySegment(segments[1])) return segments[1];
    if (isFacebookPageIdentitySegment(segments[0])) return segments[0];
  } catch {
    return trimmed;
  }
  return trimmed;
}

function deriveFacebookVanity(url?: string | null): string {
  const input = (url || "").trim();
  if (!input) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const parsed = new URL(candidate);
    if (!/(^|\.)(facebook|fb)\.com$/i.test(parsed.hostname)) return "";
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "";
    const segment = decodeURIComponent(segments[0]);
    if (/^[0-9]+$/.test(segment) || facebookReservedPaths.has(segment.toLowerCase())) return "";
    if (!/^[A-Za-z0-9.]{2,75}$/.test(segment)) return "";
    return segment;
  } catch {
    return "";
  }
}

function selectFacebookPage(page: FacebookPageSearchResult) {
  selectedFacebookToken.value = page.selectionToken ?? null;
  selectedFacebookPage.value = page;
  selectedFacebookName.value = page.name;
  clearExistingFacebook.value = false;
  isFacebookVerified.value = false;
  if (!name.value.trim() && page.name) name.value = page.name;
  if (!iconId.value && page.iconId) {
    iconId.value = page.iconId;
    previewIcon.value = page.iconId;
  }
  facebookResults.value = [];
  facebookQuery.value = "";
  facebookManualInput.value = "";
}

function clearFacebookPage() {
  selectedFacebookToken.value = null;
  selectedFacebookPage.value = null;
  selectedFacebookName.value = "";
  isFacebookVerified.value = false;
  clearExistingFacebook.value = Boolean(props.location?.socialSet?.facebookPageId);
}

function changeFacebookPage() {
  clearFacebookPage();
  facebookMode.value = "search";
  facebookQuery.value = "";
  facebookManualInput.value = "";
  facebookResults.value = [];
  errorMessage.value = "";
}

async function save() {
  if (isSaving.value || !canSave.value) return;
  isSaving.value = true;
  errorMessage.value = "";
  try {
    const request = {
      eTag: props.location?._etag,
      name: name.value.trim(),
      instagramHandle: normalizeInstagramHandle(instagramHandle.value) || null,
      iconId: iconId.value,
      facebookPageSelectionToken: selectedFacebookToken.value,
      facebookPageSelection: selectedFacebookPage.value
        ? {
            pageId: selectedFacebookPage.value.pageId,
            pageName: selectedFacebookPage.value.name,
            pageUrl: selectedFacebookPage.value.url ?? null,
            address: selectedFacebookPage.value.address ?? null,
            latitude: selectedFacebookPage.value.latitude ?? null,
            longitude: selectedFacebookPage.value.longitude ?? null,
          }
        : null,
      clearFacebookPage: clearExistingFacebook.value,
      tikTokHandle: normalizeTikTokHandle(tikTokHandle.value) || null,
    };
    const saved = props.location ? await SocialLocationsRepo.update(props.customerId, props.location.id, request) : await SocialLocationsRepo.create(props.customerId, request);
    emit("update:isVisible", false);
    emit("saved", saved);
  } catch (error: any) {
    errorMessage.value = getErrorMessage(error);
  } finally {
    isSaving.value = false;
  }
}

function getErrorMessage(error: any): string {
  const value = error?.response?.data;
  if (typeof value === "string") return value;
  return value?.detail ?? value?.title ?? error?.message ?? "Unable to save this Location.";
}
</script>

<style scoped lang="scss">
.location-editor {
  display: grid;
  gap: 12px;
}
.location-editor__header {
  display: grid;
  gap: 4px;
}
.location-editor__header h2,
.location-editor__header p {
  margin: 0;
}
.location-editor__header p {
  // color: #667085;
  font-size: 15px;
}
.location-editor__identity,
.location-editor__footer {
  display: flex;
  align-items: center;
  gap: 12px;
}
.location-editor__avatar-wrap {
  position: relative;
  flex: 0 0 auto;
  // padding: 4px 4px 0 0;
}
.location-editor__clear-icon {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #98a2b3;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
}
.location-editor__clear-icon:hover {
  color: #101828;
  border-color: #667085;
  background: #f9fafb;
}
.location-editor__name-display {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #000;
  font-size: 18px;
  font-weight: 600;
}
.location-editor__name-display span {
  // overflow: hidden;
  // text-overflow: ellipsis;
  // white-space: nowrap;
}
.location-editor__name-display button {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  padding: 3px;
  border: 0;
  background: transparent;
  color: #667085;
  cursor: pointer;
}
.location-editor__field {
  display: grid;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
}
.location-editor__field--grow {
  flex: 1;
}
.location-editor__field--facebook {
  gap: 0;
}
.location-editor__fb-label {
  margin-bottom: 7px;
}
.location-editor__handle-input {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  border: 1px solid #cbd0d7;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}
.location-editor__url-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__url-link:hover {
  text-decoration: underline;
}
.location-editor__handle-input span {
  padding: 10px 12px;
  border-right: 1px solid #d8dce2;
  color: #667085;
}
.location-editor__handle-input input {
  flex: 1;
  min-width: 0;
  padding: 10px;
  border: 0;
  outline: 0;
}
.location-editor__inline-loader {
  flex: 0 0 auto;
  margin-right: 10px;
  color: #667085;
}
.location-editor__status-row {
  margin: 0;
  font-size: 14px;
  line-height: 18px;
}
.location-editor__valid {
  color: #21883f;
  font-weight: 600;
}
.location-editor__instagram-guidance {
  color: #667085;
}
.location-editor__fb-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  padding: 4px;
  border-radius: 8px;
  background: #f2f4f7;
}
.location-editor__fb-tab {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}
.location-editor__fb-tab:hover:not(.location-editor__fb-tab--active) {
  color: #344054;
}
.location-editor__fb-tab--active {
  border-color: #d8dce2;
  background: #fff;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.location-editor__fb-panel {
  border: 1px solid #d8dce2;
  border-radius: 3px;
  background: #fff;
  overflow: hidden;
}
.location-editor__fb-search-input {
  width: 100%;
  padding: 11px 14px;
  border: 0;
  outline: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-search-input::placeholder {
  color: #98a2b3;
}
.location-editor__fb-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #e7e9ed;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-status--bare {
  padding: 0;
  border-top: 0;
}
.location-editor__fb-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid #e7e9ed;
}
.location-editor__fb-result-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.location-editor__fb-name {
  color: #101828;
  font-size: 15px;
  font-weight: 600;
}
.location-editor__fb-handle {
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-result-info small {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
}
.location-editor__fb-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d8dce2;
  border-radius: 8px;
  background: #fff;
}
.location-editor__fb-selected-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.location-editor__fb-selected-info strong {
  color: #101828;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__fb-selected-info .location-editor__url-link {
  font-size: 14px;
}
.location-editor__fb-change {
  flex: 0 0 auto;
  padding: 4px;
  border: 0;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.location-editor__fb-change:hover {
  color: #101828;
}
.location-editor__fb-verified {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 7px 0 0;
  color: #21883f;
  font-size: 14px;
  font-weight: 600;
}
.location-editor__footer {
  justify-content: flex-end;
}
</style>
